import { matchPath } from 'react-router';

/**
 * Heart-web route patterns that can be navigated in-app when opening a chat link.
 * Must stay in sync with routes in layouts/Authenticated.tsx.
 */
const IN_APP_ROUTE_PATTERNS = [
  '/',
  '/ondemand',
  '/ondemand/:contentId',
  '/session/:scheduleEventId',
  '/class/:scheduleEventId',
  '/saved',
  '/saved/:contentId',
  '/location/:locationId',
  '/video/full/:wistiaId/:id',
];

const EXOS_WEB_BASE = 'https://fit.teamexos.com';

/**
 * Normalizes Exos paths to heart-web route form (e.g. classes/ondemand -> /, classes/ondemand/:id -> /ondemand/:id).
 */
function normalizeExosPath(path: string): string {
  const trimmed = path.replace(/^\/+/, '').replace(/\/+$/, '');
  if (trimmed === 'classes/ondemand' || trimmed === 'classes') {
    return '/';
  }
  if (trimmed.startsWith('classes/ondemand/')) {
    const rest = trimmed.replace(/^classes\/ondemand\//, '');
    return rest ? `/ondemand/${rest}` : '/ondemand';
  }
  return trimmed ? `/${trimmed}` : '/';
}

function pathMatchesInAppRoute(path: string): boolean {
  const normalized = normalizeExosPath(path);
  return IN_APP_ROUTE_PATTERNS.some(
    (pattern) => matchPath({ path: pattern, end: true }, normalized) !== null,
  );
}

/**
 * Result of classifying a chat preview URL: how the UI should treat it.
 * - in-app: navigate inside heart-web
 * - external: open in browser (new tab); use for https or exos: links
 */
export type ChatPreviewLinkBehavior =
  | { type: 'in-app'; path: string }
  | { type: 'external'; url: string };

/**
 * Classifies a chat preview URL so the UI can open it in-app or in a new tab.
 * exosfit:// scheme links are converted to their web equivalent and opened externally.
 * fit.teamexos.com links matching known routes are classified as in-app; all others external.
 */
export function getChatPreviewLinkBehavior(
  url: string,
): ChatPreviewLinkBehavior | null {
  const trimmed = url?.trim();
  if (!trimmed) {
    return null;
  }

  if (trimmed.startsWith('exosfit:')) {
    const pathPart = trimmed.slice(8).replace(/^\/+/, '');
    const webUrl = pathPart ? `${EXOS_WEB_BASE}/${pathPart}` : EXOS_WEB_BASE;
    return { type: 'external', url: webUrl };
  }

  try {
    const parsed = new URL(trimmed);
    const isExosWeb =
      parsed.protocol === 'https:' &&
      (parsed.hostname === 'fit.teamexos.com' ||
        parsed.hostname === 'www.fit.teamexos.com');
    if (!isExosWeb) {
      return { type: 'external', url: trimmed };
    }
    const path = parsed.pathname || '/';
    const normalized = normalizeExosPath(path);
    if (pathMatchesInAppRoute(path)) {
      return { type: 'in-app', path: normalized };
    }
    return { type: 'external', url: trimmed };
  } catch {
    return { type: 'external', url: trimmed };
  }
}

/**
 * Segment of message text: either plain text or a URL.
 */
type MessageTextSegment =
  | { type: 'text'; value: string }
  | { type: 'url'; value: string; url: string };

/**
 * Regex to find URLs in message text: https(s)://... or exos:...
 */
const URL_IN_TEXT_REGEX = /(https?:\/\/[^\s]+)|(exosfit:[^\s]*)/gi;

/**
 * Parses message text into segments of plain text and URLs.
 * Used to render bubble content with inline links or inline QR for mobile-only URLs.
 */
export function parseMessageTextForUrls(text: string): MessageTextSegment[] {
  const trimmed = text?.trim() ?? '';
  if (!trimmed) {
    return [];
  }
  const segments: MessageTextSegment[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  const re = new RegExp(URL_IN_TEXT_REGEX.source, URL_IN_TEXT_REGEX.flags);
  while ((match = re.exec(trimmed)) !== null) {
    const fullMatch = match[0];
    const start = match.index;
    if (start > lastIndex) {
      segments.push({ type: 'text', value: trimmed.slice(lastIndex, start) });
    }
    segments.push({ type: 'url', value: fullMatch, url: fullMatch });
    lastIndex = re.lastIndex;
  }
  if (lastIndex < trimmed.length) {
    segments.push({ type: 'text', value: trimmed.slice(lastIndex) });
  }
  return segments.length > 0 ? segments : [{ type: 'text', value: trimmed }];
}

export interface OpenChatPreviewUrlDeps {
  openExternal: (url: string) => void;
}

/**
 * Opens a chat preview URL in a new tab so the member keeps their chat context.
 * In-app links are opened as full URLs (origin + path) in a new tab.
 */
export function openChatPreviewUrl(
  url: string,
  deps: OpenChatPreviewUrlDeps,
): void {
  const { openExternal } = deps;
  const behavior = getChatPreviewLinkBehavior(url);
  if (!behavior) {
    return;
  }
  if (behavior.type === 'in-app') {
    openExternal(`${window.location.origin}${behavior.path}`);
    return;
  }
  openExternal(behavior.url);
}

const CHAT_PREVIEW_SUBTITLE_MAX_LENGTH = 65;

function truncateForSubtitle(str: string, max: number): string {
  if (str.length <= max) {
    return str;
  }
  return `${str.slice(0, max - 3)}...`;
}

/**
 * Minimal preview shape for subtitle derivation (no GraphQL dependency).
 */
interface ChatPreviewSubtitleInput {
  url?: string | null;
  title?: string | null;
  description?: string | null;
}

/**
 * Derives display subtitle for a chat link preview.
 * Exos URLs (fit.teamexos.com or exosfit: scheme): description only (never the raw URL).
 * External URLs: description, falling back to the URL.
 * Truncates to CHAT_PREVIEW_SUBTITLE_MAX_LENGTH with ellipsis.
 */
export function getChatPreviewSubtitle(
  preview: ChatPreviewSubtitleInput,
  behavior: ChatPreviewLinkBehavior | null,
): string {
  const description = preview.description?.trim() ?? '';
  const url = preview.url?.trim() ?? '';
  let isExosUrl = url.startsWith('exosfit:');
  if (!isExosUrl) {
    try {
      const parsed = new URL(url);
      isExosUrl =
        parsed.hostname === 'fit.teamexos.com' ||
        parsed.hostname === 'www.fit.teamexos.com';
    } catch {
      // not a valid URL; leave isExosUrl false
    }
  }
  const isExosContext = behavior?.type === 'in-app' || isExosUrl;
  const text = isExosContext ? description : description || url;
  return truncateForSubtitle(text, CHAT_PREVIEW_SUBTITLE_MAX_LENGTH);
}

/**
 * True when message text is only URL(s) with no other text – show preview cards only, not bubble.
 */
export function isLinkOnlyMessage(text: string): boolean {
  const segments = parseMessageTextForUrls(text);
  if (segments.length === 0) {
    return true;
  }
  return segments.every(
    (s) => s.type === 'url' || (s.type === 'text' && !s.value.trim()),
  );
}

/**
 * Preview item shape for chat message (matches LinkPreviewData from LinkPreview component).
 */
export interface ChatMessagePreview {
  url: string;
  title?: string | null;
  description?: string | null;
  image?: { url?: string; preview?: string | null } | null;
}

/** Message-like object that may have a previews array from the API. */
interface MessageWithPreviews {
  previews?: Array<{
    url?: string | null;
    title?: string | null;
    description?: string | null;
    image?: { url?: string; preview?: string | null } | null;
  }> | null;
}

/**
 * Extracts valid link previews from a message (non-empty url only).
 * Single place for message → preview shape so CoachChat doesn't cast.
 */
export function getMessagePreviews(
  message: MessageWithPreviews,
): ChatMessagePreview[] {
  const raw = message.previews ?? [];
  return raw
    .filter(
      (
        p,
      ): p is {
        url: string;
        title?: string | null;
        description?: string | null;
        image?: { url?: string; preview?: string | null } | null;
      } => (p.url?.trim() ?? '').length > 0,
    )
    .map((p) => ({
      url: (p.url ?? '').trim(),
      title: p.title ?? null,
      description: p.description ?? null,
      image: p.image
        ? { url: p.image.url, preview: p.image.preview ?? null }
        : null,
    }));
}
